import { useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { useAuthContext } from "../auth/AuthContext";
import { friendlyAuthError } from "../auth/authErrors";
import type {
  AuthProviderConfig,
  AuthProviderId,
  B2cConfig,
} from "../auth/authProviders";
import { useDarkMode } from "../theme/useDarkMode";

type Active =
  | { kind: "picker" }
  | { kind: "entra" }
  | { kind: "b2c"; provider: B2cConfig };

export default function LoginPage() {
  const auth = useAuthContext();
  const [searchParams] = useSearchParams();
  // Set by SessionGuard when it redirects after expired session.
  const sessionExpired = searchParams.get("reason") === "expired";

  const [active, setActive] = useState<Active>({ kind: "picker" });
  const [error, setError] = useState<string | null>(null);

  if (auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  function pick(provider: AuthProviderConfig) {
    setError(null);
    if (provider.id === "entra") {
      setActive({ kind: "entra" });
      void runEntra();
      return;
    }
    setActive({ kind: "b2c", provider });
  }

  async function runEntra() {
    setError(null);
    try {
      await auth.loginWithEntra();
    } catch (e) {
      setError(friendlyAuthError(e, "Sign in failed. Please try again."));
      setActive({ kind: "picker" });
    }
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 font-sans text-foreground relative bg-gradient-to-br from-gray-50 via-white to-gray-100/60 dark:from-background dark:via-background dark:to-background">
      {/*
        Card shadow mirrors Provider Portal's login card — a stacked
        triple-shadow with a plum tint on the outer rings. Gives the card
        visible lift against the soft background-gradient without the
        harsh black drop-shadow the default `shadow-xl` produces.
      */}
      <div className="w-full max-w-sm bg-gradient-to-br from-white via-white/95 to-gray-100/70 dark:from-card dark:via-card dark:to-card text-card-foreground rounded-2xl border border-border/60 p-8 sm:p-10 flex flex-col items-center gap-6 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.22),_0_0_40px_-12px_rgba(74,8,48,0.06),_0_-3px_24px_-10px_rgba(138,21,88,0.04)]">
        <Brand />

        {sessionExpired && (
          <div
            role="status"
            className="w-full rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm text-muted-foreground text-center"
          >
            Your session timed out. Sign in again to continue.
          </div>
        )}

        <div className="w-full border-t border-border" />

        {active.kind === "picker" && (
          <Picker providers={auth.enabledProviders} onPick={pick} />
        )}

        {active.kind === "entra" && (
          <EntraPendingCard onBack={() => setActive({ kind: "picker" })} />
        )}

        {active.kind === "b2c" && (
          <B2cForm
            provider={active.provider}
            canGoBack={auth.enabledProviders.length > 1}
            onBack={() => setActive({ kind: "picker" })}
            onSubmit={async (email, password) => {
              setError(null);
              await auth.loginWithB2c(active.provider.id, email, password);
            }}
          />
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive font-medium m-0 text-center">
            {error}
          </p>
        )}
      </div>
      <LoginFooter />
      <BuildBadge />
    </div>
  );
}

function Brand() {
  const { isDark } = useDarkMode();
  const symbol = isDark
    ? "/images/logo_A_white.svg"
    : "/images/logo_A_plum.svg";
  return (
    <div className="flex flex-row items-center justify-center gap-3">
      <div
        style={{
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 9999,
          background: isDark
            ? "linear-gradient(to bottom, rgba(198, 107, 162, 0.25), transparent)"
            : "linear-gradient(to bottom, rgba(108, 12, 70, 0.12), transparent)",
        }}
      >
        <img
          src={symbol}
          alt="Astrana Health Logo"
          style={{ width: 20 }}
        />
      </div>
      {/*
        Gradient text matches Provider Portal's onboarding login — plum
        ramp, clipped to the glyph. Fallback color is the solid brand
        plum for rendering contexts that don't support background-clip.
      */}
      <h1 className="text-2xl font-bold m-0 bg-gradient-to-r from-primary to-[hsl(324,60%,45%)] dark:from-[hsl(324,60%,70%)] dark:to-[hsl(324,80%,82%)] bg-clip-text text-transparent">
        Atlas Copilot
      </h1>
    </div>
  );
}

function Picker({
  providers,
  onPick,
}: {
  providers: AuthProviderConfig[];
  onPick: (p: AuthProviderConfig) => void;
}) {
  if (providers.length === 0) {
    return (
      <div className="text-center">
        <p className="text-sm font-semibold text-foreground m-0">
          No sign-in methods configured.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-2.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground text-center">
        Sign in with
      </p>
      {providers.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onPick(p)}
          className={providerButtonClass(p.id)}
        >
          <ProviderIcon id={p.id} />
          <span>{p.label}</span>
        </button>
      ))}
    </div>
  );
}

function EntraPendingCard({ onBack }: { onBack: () => void }) {
  return (
    <div className="w-full flex flex-col items-center gap-3">
      <Spinner />
      <p className="text-sm text-muted-foreground m-0">
        Redirecting you to sign in…
      </p>
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        ← Choose a different method
      </button>
    </div>
  );
}

function B2cForm({
  provider,
  canGoBack,
  onBack,
  onSubmit,
}: {
  provider: B2cConfig;
  canGoBack: boolean;
  onBack: () => void;
  onSubmit: (email: string, password: string) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setPending(true);
    setLocalError(null);
    try {
      await onSubmit(email.trim(), password);
      // Our login is a fetch() + in-app nav — there's no traditional
      // page-navigation event for Chrome's password manager to hook
      // onto, so autosave prompts don't fire on their own. The
      // Credential Management API lets us hand the browser the
      // credential explicitly after a successful auth; Chrome then
      // offers to save. Fire-and-forget: the prompt is async and we
      // don't want to block the redirect on it. Third-party password
      // managers (1Password / Bitwarden) mostly don't listen for this,
      // so they still rely on their own form-field heuristics — the
      // autoComplete + id + name attributes below are the hints that
      // keep them happy.
      void maybeSaveCredential(email.trim(), password);
    } catch (err) {
      setLocalError(
        friendlyAuthError(err, "Sign in failed. Check your credentials and try again."),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground m-0">
          {provider.label}
        </p>
      </div>

      {/*
        id + name on the inputs are load-bearing for third-party
        password managers: 1Password / Bitwarden / LastPass pattern-
        match on them. autoComplete drives Chrome's built-in.
      */}
      <label htmlFor="login-email" className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/80">
          Email
        </span>
        <input
          id="login-email"
          name="username"
          type="email"
          autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-input bg-background px-4 py-3 text-base text-foreground placeholder:text-muted-foreground hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all disabled:opacity-60"
          disabled={pending}
        />
      </label>

      <label htmlFor="login-password" className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/80">
          Password
        </span>
        <div className="relative">
          <input
            id="login-password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-input bg-background pl-4 pr-12 py-3 text-base text-foreground placeholder:text-muted-foreground hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all disabled:opacity-60"
            disabled={pending}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md p-1 transition-colors"
            tabIndex={pending ? -1 : 0}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      </label>

      {localError && (
        <p role="alert" className="text-sm text-destructive font-medium m-0">
          {localError}
        </p>
      )}

      {/*
        Gradient CTA mirrors Provider Portal's login button. `hover:
        brightness-110` + a plum-tinted drop-shadow gives the button a
        subtle lift on hover without needing a darker solid fill
        underneath.
      */}
      <button
        type="submit"
        disabled={pending}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white bg-gradient-to-br from-[#8a1558] to-[#c42a83] hover:brightness-110 hover:shadow-[0_4px_14px_rgba(138,21,88,0.3)] text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {pending ? "Signing in…" : "Sign in"}
      </button>

      {canGoBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          disabled={pending}
        >
          ← Choose a different method
        </button>
      )}
    </form>
  );
}

function providerButtonClass(id: AuthProviderId): string {
  // Entra gets the brand gradient CTA (same treatment as the B2C sign-in
  // button below); B2C providers on the picker get a softer secondary
  // treatment so the two don't fight each other for attention.
  const common =
    "w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-xl text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background";
  if (id === "entra") {
    return (
      common +
      " text-white bg-gradient-to-br from-[#8a1558] to-[#c42a83] hover:brightness-110 hover:shadow-[0_4px_14px_rgba(138,21,88,0.3)] focus-visible:ring-ring"
    );
  }
  return (
    common +
    " bg-card border border-border text-foreground hover:border-primary hover:text-primary focus-visible:ring-ring"
  );
}

function ProviderIcon({ id }: { id: AuthProviderId }) {
  if (id === "entra") {
    return (
      <img
        src="/images/microsoft-icon.svg"
        alt=""
        aria-hidden="true"
        className="w-5 h-5 shrink-0"
      />
    );
  }
  // Generic id-card glyph for B2C — kept as an inline SVG so we don't depend
  // on an extra asset per provider.
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 10h4M7 14h6" />
      <circle cx="16" cy="12" r="2" />
    </svg>
  );
}

// Inline Eye / EyeOff icons — Lucide shapes, kept inline so we don't pull
// the full icon lib into the login-screen bundle. Match the styling Provider
// Portal uses on its onboarding login screen.
function EyeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

function Spinner() {
  return (
    <div
      className="w-7 h-7 rounded-full border-[3px] border-border border-t-primary animate-spin"
      aria-hidden="true"
    />
  );
}

// URLs are the live Astrana notices, same PDFs Provider Portal's
// onboarding login uses. Intentionally omit PP's Help Center link —
// that Notion page is Provider-Portal-specific content.
const PRIVACY_URL =
  "https://www.astranacare.com/wp-content/uploads/2024/06/NOTICE-OF-PRIVACY-PRACTICES_ASTRANA-CARE.pdf";
const NON_DISCRIMINATION_URL =
  "https://www.astranahealthmanagement.com/wp-content/uploads/2025/05/notice-non-discrimination-astrana.pdf";

// Two-row footer: legal links on one line, copyright on a quiet second
// line underneath. Puts the interactive items together and de-emphasises
// the copyright, which is only there for compliance.
function LoginFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="mt-6 flex flex-col items-center gap-1.5">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <a
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground transition-colors"
        >
          Privacy
        </a>
        <span aria-hidden="true">·</span>
        <a
          href={NON_DISCRIMINATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground transition-colors"
        >
          Non-Discrimination
        </a>
      </div>
      <div className="text-[11px] text-muted-foreground/70">
        © {year} Astrana Health
      </div>
    </footer>
  );
}

function BuildBadge() {
  // `VITE_APP_VERSION` is stamped in CI (see
  // .github/workflows/dev_dev-member-360-standalone.yml). When running
  // locally without the var set, render nothing — "Build: dev" was noise.
  const version = import.meta.env.VITE_APP_VERSION;
  if (!version) return null;
  return (
    <div className="absolute bottom-2 right-3 text-[11px] text-muted-foreground/60 font-mono">
      Build: {version}
    </div>
  );
}

/**
 * Hand a successful credential to the browser's built-in password
 * manager via the Credential Management API. Chrome wires this up to
 * its autosave prompt; Safari ignores the call gracefully; third-
 * party password managers don't listen to this API and rely on their
 * own heuristics (hence the `id`/`name`/`autoComplete` attributes on
 * the inputs above).
 *
 * All failures are silent — we're purely trying to be friendlier
 * to a working API, never blocking auth on it.
 */
async function maybeSaveCredential(id: string, password: string): Promise<void> {
  try {
    const w = window as Window & {
      PasswordCredential?: new (init: {
        id: string;
        password: string;
        name?: string;
      }) => Credential;
    };
    if (!w.PasswordCredential || !navigator.credentials?.store) return;
    const cred = new w.PasswordCredential({ id, password, name: id });
    await navigator.credentials.store(cred);
  } catch {
    // best-effort
  }
}
